/**
 * appApi — RTK Query service for ALL read-only (GET) endpoints.
 *
 * ARCHITECTURE RULES:
 *  ✅ Add every GET endpoint here as a `builder.query`.
 *  ✅ Consume in components via the auto-generated `useGet___Query` hook.
 *  ❌ Never add POST / PATCH / DELETE endpoints here — those are direct Axios
 *     calls inside local component state (see rule in project docs).
 *
 * HOW TO ADD A NEW ENDPOINT:
 *   1. Add `getXxx: builder.query({ query: () => '/your-route', providesTags: [...] })`
 *   2. Export the hook from the bottom of this file.
 *   3. Import and call the hook inside your component — done.
 *
 * CACHE INVALIDATION:
 *   After a mutation in a component (e.g., creating a customer), call
 *   `dispatch(appApi.util.invalidateTags(['Customers']))` to refetch.
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { selectToken, logout } from "./authSlice";
import { selectActiveProjectId } from "./projectSlice";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${API_BASE_URL}/api`,
  prepareHeaders: (headers, { getState }) => {
    const token = selectToken(getState());
    if (token) headers.set("Authorization", `Bearer ${token}`);
    // A query that explicitly set x-project-id (e.g. viewing/editing a
    // project other than the globally active one) wins over the ambient
    // active-project header — only fall back to it when unset.
    if (!headers.has("x-project-id")) {
      const projectId = selectActiveProjectId(getState());
      if (projectId) headers.set("x-project-id", projectId);
    }
    return headers;
  },
});

// Wraps the raw query so a 403 { reason: "account_disabled" } — fired when a
// super-admin deactivates this user mid-session — logs them out immediately,
// even on GET-only screens that never go through the Axios interceptor.
const baseQueryWithAuthCheck = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (
    result.error?.status === 403 &&
    result.error?.data?.reason === "account_disabled"
  ) {
    api.dispatch(logout());
    window.location.href = "/login";
  }
  return result;
};

export const appApi = createApi({
  reducerPath: "appApi",

  // Automatically injects the JWT from the Redux store on every request.
  // No component needs to manually build Authorization headers.
  baseQuery: baseQueryWithAuthCheck,

  // Tag registry — used to tie mutations to cache invalidations.
  tagTypes: [
    "BackdateGrants",
    "RealEstateTeams",
    "NetplusTeams",
    "Dashboard",
    "CurrentUser",
    "Customers",
    "Vehicles",
    "Consultants",
    "Blogs",
    "Sessions",
    "Staff",
    "Companies",
    "CompanyCategories",
    "Projects",
    "UserProjects",
    "Suppliers",
    "ContractorCategories",
    "Contractors",
    "Expenses",
    "FinanceCategories",
    "LabourTypes",
    "LabourPayments",
    "EquipmentProjects",
    "WorkProgressDropdowns",
    "WorkProgressReports",
    "VehicleAssignments",
    "SKUs",
    "StoreTransactions",
    "StockEditRequests",
    "Equipment",
    "Assets",
    "AttendanceCalendar",
    "Holidays",
    "AttendanceRoster",
    "WorkplaceSettings",
    "SelfieConfig",
    "Leaves",
    "MdSchedules",
    "ManagingDirectors",
    "Advances",
    "CpDatabase",
    "ChatConversations",
    "ChatContacts",
    "NetplusReceipts",
    "NetplusModem",
    "NetplusCustomerStatus",
    "NetplusIncentive",
    "NetplusReconciliation",
    "NetplusComplaints",
    "NetplusBankRecon",
    "Sales",
    "PerformanceReview",
    "SalarySheet",
    "Imprest",
    "MovementRegister",
  ],

  endpoints: (builder) => ({
    // ── Current User (fresh permissions from DB) ──────────────────────────────
    getCurrentUser: builder.query({
      query: () => "/me",
      providesTags: ["CurrentUser"],
    }),

    // ── Dashboard ─────────────────────────────────────────────────────────────
    getDashboardStats: builder.query({
      query: () => "/admin/dashboard",
      providesTags: ["Dashboard"],
      // RTK Query caches this for 60 s by default; pollingInterval can be set
      // per-component via the hook's options argument if live data is needed.
    }),

    // ── Customers ─────────────────────────────────────────────────────────────
    getAllCustomers: builder.query({
      query: (params = "") => `/get-users${params}`,
      providesTags: ["Customers"],
    }),

    getCustomer: builder.query({
      query: (id) => `/get-user/${id}`,
      providesTags: (result, error, id) => [{ type: "Customers", id }],
    }),

    // ── Staff ─────────────────────────────────────────────────────────────────
    getAllStaff: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        allProjects = false,
      } = {}) => ({
        url: "/get-staff",
        params: {
          page,
          limit,
          search,
          ...(allProjects ? { allProjects: 1 } : {}),
        },
      }),
      providesTags: ["Staff"],
    }),

    getStaffById: builder.query({
      query: (id) => `/get-user/${id}`,
      providesTags: (result, error, id) => [{ type: "Staff", id }],
    }),

    getTechnicalEmployees: builder.query({
      query: () => "/employees/technical",
      providesTags: ["Staff"],
    }),

    // Project Manager combined report — a PM gets their own team; a
    // super-admin passes managerId (and gets the list of PMs back).
    getPmCombinedReport: builder.query({
      query: ({ managerId = "" } = {}) => ({
        url: "/staff-reporting/pm-combined",
        params: { managerId: managerId || undefined },
      }),
      providesTags: ["WorkProgressReports"],
    }),

    getStaffReports: builder.query({
      query: ({
        employeeId = "",
        role = "",
        date = "",
        status = "",
        formType = "",
        unit = "",
        page = 1,
        limit = 200,
      } = {}) => ({
        url: "/staff-reporting",
        params: { employeeId, role, date, status, formType, unit, page, limit },
      }),
      providesTags: ["WorkProgressReports"],
    }),

    getStaffReportById: builder.query({
      query: (id) => `/staff-reporting/${id}`,
      providesTags: (result, error, id) => [
        { type: "WorkProgressReports", id },
      ],
    }),

    getMovementRegister: builder.query({
      query: ({
        employeeId = "",
        from = "",
        to = "",
        page = 1,
        limit = 50,
      } = {}) => ({
        url: "/movement-register",
        params: { employeeId, from, to, page, limit },
      }),
      providesTags: ["MovementRegister"],
    }),

    getEmployeeStaffReports: builder.query({
      query: (employeeId) => `/staff-reporting/employee/${employeeId}`,
      providesTags: (result, error, employeeId) => [
        { type: "WorkProgressReports", employeeId },
      ],
    }),

    // Past days a super-admin has unlocked for the signed-in employee.
    getMyBackdateGrants: builder.query({
      query: () => "/staff-reporting/backdate-grants/mine",
      providesTags: ["BackdateGrants"],
    }),

    // ── Team Real Estate ──────────────────────────────────────────────────────
    // super-admin: every team; Manager Real Estate: only his own teams.
    getRealEstateTeams: builder.query({
      query: (params = {}) => ({ url: "/real-estate-teams", params }),
      providesTags: ["RealEstateTeams"],
    }),
    // super-admin: pickers (active managers + eligible staff) for the team form.
    getRealEstateTeamOptions: builder.query({
      query: () => "/real-estate-teams/options",
      providesTags: ["RealEstateTeams"],
    }),
    // Combined report of the team(s): { teamId?, from?, to? } (YYYY-MM-DD).
    getRealEstateClubReport: builder.query({
      query: ({ teamId = "", from = "", to = "" } = {}) => ({
        url: "/real-estate-teams/club-report",
        params: {
          teamId: teamId || undefined,
          from: from || undefined,
          to: to || undefined,
        },
      }),
      providesTags: ["RealEstateTeams", "WorkProgressReports"],
    }),

    // Super-admin daily summary: Netplus + Real Estate + Revenue + Construction.
    getDailySummary: builder.query({
      query: (date = "") => ({
        url: "/reports/daily-summary",
        params: { date: date || undefined },
      }),
      providesTags: ["NetplusTeams", "RealEstateTeams", "WorkProgressReports"],
    }),

    // ── Team Netplus ──────────────────────────────────────────────────────────
    getNetplusTeams: builder.query({
      query: (params = {}) => ({ url: "/netplus-teams", params }),
      providesTags: ["NetplusTeams"],
    }),
    getNetplusTeamOptions: builder.query({
      query: () => "/netplus-teams/options",
      providesTags: ["NetplusTeams"],
    }),
    // Combined "Netplus Reports": { teamId?, from?, to? } (YYYY-MM-DD).
    getNetplusClubReport: builder.query({
      query: ({ teamId = "", from = "", to = "" } = {}) => ({
        url: "/netplus-teams/club-report",
        params: {
          teamId: teamId || undefined,
          from: from || undefined,
          to: to || undefined,
        },
      }),
      providesTags: ["NetplusTeams", "WorkProgressReports"],
    }),

    // Super-admin: active unlocked days (optionally for one employee).
    getBackdateGrants: builder.query({
      query: (employeeId) => ({
        url: "/staff-reporting/backdate-grants",
        params: { employeeId: employeeId || undefined },
      }),
      providesTags: ["BackdateGrants"],
    }),

    // ── Advances ──────────────────────────────────────────────────────────────
    getAdvanceSummaries: builder.query({
      query: () => "/advances",
      providesTags: ["Advances"],
    }),

    getEmployeeAdvanceLedger: builder.query({
      query: (employeeId) => `/advances/employee/${employeeId}`,
      providesTags: (result, error, employeeId) => [
        { type: "Advances", employeeId },
      ],
    }),

    // ── CP Database ───────────────────────────────────────────────────────────
    getCpDatabase: builder.query({
      query: () => "/cp-database",
      providesTags: ["CpDatabase"],
    }),

    // ── Chat ──────────────────────────────────────────────────────────────────
    getChatContacts: builder.query({
      query: () => "/chat/contacts",
      providesTags: ["ChatContacts"],
    }),
    getChatConversations: builder.query({
      query: () => "/chat/conversations",
      providesTags: ["ChatConversations"],
    }),
    getChatUnreadCount: builder.query({
      query: () => "/chat/unread-count",
      providesTags: ["ChatConversations"],
    }),

    // ── Netplus Receipts ──────────────────────────────────────────────────────
    getNetplusReceipts: builder.query({
      query: ({
        page = 1,
        limit = 20,
        type = "",
        dateFrom = "",
        dateTo = "",
      } = {}) => ({
        url: "/netplus/receipts",
        params: { page, limit, type, dateFrom, dateTo },
      }),
      providesTags: ["NetplusReceipts"],
    }),

    getNetplusSummary: builder.query({
      query: () => "/netplus/summary",
      providesTags: ["NetplusReceipts"],
    }),

    getNetplusTransfers: builder.query({
      query: () => "/netplus/transfers",
      providesTags: ["NetplusReceipts"],
    }),

    // ── Netplus Modem Recovery & Recharges (Satti & Co only) ──────────────────
    getNetplusModemEntries: builder.query({
      query: ({ month = "", date = "", dateFrom = "", dateTo = "" } = {}) => ({
        url: "/netplus/modem-status",
        params: { month, date, dateFrom, dateTo },
      }),
      providesTags: ["NetplusModem"],
    }),

    getNetplusModemSummary: builder.query({
      query: () => "/netplus/modem-status/summary",
      providesTags: ["NetplusModem"],
    }),

    // ── Netplus Customer Status Report ───────────────────────────────────────
    getNcsPartners: builder.query({
      // A partner code is its own tenant key, independent of the active
      // project — the picker shows every registered partner, not just the
      // ones under whichever project happens to be selected right now.
      query: () => ({
        url: "/netplus/customer-status/partners",
        params: { all: true },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsDashboard: builder.query({
      query: ({ partnerCode, window = 7 } = {}) => ({
        url: "/netplus/customer-status/dashboard",
        params: { partnerCode, window },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsTrend: builder.query({
      query: ({ partnerCode, days = 30 } = {}) => ({
        url: "/netplus/customer-status/trend",
        params: { partnerCode, days },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsDeactivationForecast: builder.query({
      query: ({ partnerCode, days = 7, withList = false } = {}) => ({
        url: "/netplus/customer-status/deactivation-forecast",
        params: { partnerCode, days, withList },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsCustomers: builder.query({
      query: ({
        partnerCode,
        bucket = "",
        status = "",
        q = "",
        from = "",
        to = "",
        dateField = "",
        page = 1,
        limit = 50,
        sort = "expiryDate",
        dir = "asc",
      } = {}) => ({
        url: "/netplus/customer-status/customers",
        params: {
          partnerCode,
          bucket,
          status,
          q,
          from,
          to,
          dateField,
          page,
          limit,
          sort,
          dir,
        },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsNewConnections: builder.query({
      query: ({
        partnerCode,
        year = "",
        month = "",
        from = "",
        to = "",
        q = "",
        page = 1,
        limit = 50,
      } = {}) => ({
        url: "/netplus/customer-status/new-connections",
        params: { partnerCode, year, month, from, to, q, page, limit },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsCustomer: builder.query({
      query: ({ partnerCode, customerKey } = {}) => ({
        url: `/netplus/customer-status/customers/${customerKey}`,
        params: { partnerCode },
      }),
      providesTags: (r, e, arg) => [
        { type: "NetplusCustomerStatus", id: arg?.customerKey },
      ],
    }),
    getNcsTransitions: builder.query({
      query: ({
        partnerCode,
        type = "",
        from = "",
        to = "",
        customerKey = "",
        page = 1,
        limit = 100,
      } = {}) => ({
        url: "/netplus/customer-status/transitions",
        params: { partnerCode, type, from, to, customerKey, page, limit },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsFollowups: builder.query({
      query: ({
        partnerCode,
        state = "",
        assignedTo = "",
        bucket = "",
        sort = "daysDown",
        q = "",
        from = "",
        to = "",
        dateField = "",
        page = 1,
        limit = 50,
      } = {}) => ({
        url: "/netplus/customer-status/followups",
        params: {
          partnerCode,
          state,
          assignedTo,
          bucket,
          sort,
          q,
          from,
          to,
          dateField,
          page,
          limit,
        },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsFollowupSummary: builder.query({
      query: ({ partnerCode } = {}) => ({
        url: "/netplus/customer-status/followups/summary",
        params: { partnerCode },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsStillInactiveSinceYesterday: builder.query({
      query: ({ partnerCode, date = "" } = {}) => ({
        url: "/netplus/customer-status/followups/still-inactive-since-yesterday",
        params: { partnerCode, date },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsActivated: builder.query({
      query: ({ partnerCode, window = 7 } = {}) => ({
        url: "/netplus/customer-status/followups/activated",
        params: { partnerCode, window },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsUploads: builder.query({
      query: ({ partnerCode, limit = 60 } = {}) => ({
        url: "/netplus/customer-status/uploads",
        params: { partnerCode, limit },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsRechargedToday: builder.query({
      query: ({ partnerCode } = {}) => ({
        url: "/netplus/customer-status/recharged-today",
        params: { partnerCode },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsDailyReport: builder.query({
      query: ({ partnerCode } = {}) => ({
        url: "/netplus/customer-status/daily-report",
        params: { partnerCode },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsOverdueBuckets: builder.query({
      query: ({ partnerCode, withList } = {}) => ({
        url: "/netplus/customer-status/overdue-buckets",
        params: { partnerCode, withList: withList ? "true" : undefined },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsDiag: builder.query({
      query: ({ partnerCode } = {}) => ({
        url: "/netplus/customer-status/diag",
        params: { partnerCode },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsTimeseries: builder.query({
      query: ({ partnerCode, grain = "month" } = {}) => ({
        url: "/netplus/customer-status/timeseries",
        params: { partnerCode, grain },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsIncentive: builder.query({
      query: ({ view, month, year, partnerCode } = {}) => ({
        url: "/netplus/customer-status/incentive",
        params: { view, month, year, partnerCode },
      }),
      providesTags: ["NetplusIncentive"],
    }),
    getNcsIncentiveNewConnections: builder.query({
      query: ({ from, to, partner, partnerCode } = {}) => ({
        url: "/netplus/customer-status/incentive/new-connections",
        params: { from, to, partner, partnerCode },
      }),
      providesTags: ["NetplusIncentive"],
    }),
    getNcsIncentivePlans: builder.query({
      query: () => "/netplus/customer-status/incentive/plans",
      providesTags: ["NetplusIncentive"],
    }),
    getNcsIncentiveConfig: builder.query({
      query: () => "/netplus/customer-status/incentive/config",
      providesTags: ["NetplusIncentive"],
    }),
    getNcsTeam: builder.query({
      query: ({ all = false } = {}) => ({
        url: "/netplus/customer-status/team",
        params: { all },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsModemRecovery: builder.query({
      query: ({
        partnerCode,
        state = "",
        assignedTo = "",
        assignedBy = "",
        name = "",
        q = "",
        from = "",
        to = "",
        page = 1,
        limit = 50,
      } = {}) => ({
        url: "/netplus/customer-status/modem-recovery",
        params: {
          partnerCode,
          state,
          assignedTo,
          assignedBy,
          name,
          q,
          from,
          to,
          page,
          limit,
        },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    // Field-sheet reconciliation — local-disk only (see controllers/netplusReconciliation.js), super-admin only.
    getReconciliationRuns: builder.query({
      query: () => "/netplus/customer-status/reconciliation",
      providesTags: ["NetplusReconciliation"],
    }),
    getReconciliationRun: builder.query({
      query: (runId) => `/netplus/customer-status/reconciliation/${runId}`,
      providesTags: (result, error, runId) => [{ type: "NetplusReconciliation", id: runId }],
    }),
    getNcsFreelancerPayments: builder.query({
      query: ({ partnerCode, page = 1, limit = 100 } = {}) => ({
        url: "/netplus/customer-status/freelancer-payments",
        params: { partnerCode, page, limit },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsAudit: builder.query({
      query: ({
        partnerCode = "",
        actorId = "",
        action = "",
        entityType = "",
        customerKey = "",
        from = "",
        to = "",
        page = 1,
        limit = 100,
      } = {}) => ({
        url: "/netplus/customer-status/audit",
        params: {
          partnerCode,
          actorId,
          action,
          entityType,
          customerKey,
          from,
          to,
          page,
          limit,
        },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsPerformance: builder.query({
      query: ({ partnerCode, from = "", to = "" } = {}) => ({
        url: "/netplus/customer-status/performance",
        params: { partnerCode, from, to },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    getNcsEmployeeDaily: builder.query({
      query: ({ partnerCode = "", from = "", to = "", actorId = "" } = {}) => ({
        url: "/netplus/customer-status/employee-daily",
        params: { partnerCode, from, to, actorId },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),
    // Any signed-in employee's own daily activity — server forces this to
    // the caller's own actorId regardless of what's passed here.
    getNcsMyDaily: builder.query({
      query: ({ partnerCode = "", from = "", to = "" } = {}) => ({
        url: "/netplus/customer-status/my-daily",
        params: { partnerCode, from, to },
      }),
      providesTags: ["NetplusCustomerStatus"],
    }),

    // ── Netplus Complaints ─────────────────────────────────────────────────
    getNcmpComplaints: builder.query({
      query: ({
        partnerCode,
        status = "",
        q = "",
        from = "",
        to = "",
        page = 1,
        limit = 50,
      } = {}) => ({
        url: "/netplus/complaints",
        params: { partnerCode, status, q, from, to, page, limit },
      }),
      providesTags: ["NetplusComplaints"],
    }),
    getNcmpUploads: builder.query({
      query: ({ partnerCode } = {}) => ({
        url: "/netplus/complaints/uploads",
        params: { partnerCode },
      }),
      providesTags: ["NetplusComplaints"],
    }),
    getNcmpSummary: builder.query({
      query: ({ partnerCode } = {}) => ({
        url: "/netplus/complaints/summary",
        params: { partnerCode },
      }),
      providesTags: ["NetplusComplaints"],
    }),
    getNcmpResolvers: builder.query({
      query: ({ partnerCode } = {}) => ({
        url: "/netplus/complaints/resolvers",
        params: { partnerCode },
      }),
      providesTags: ["NetplusComplaints"],
    }),

    // ── Sales ──────────────────────────────────────────────────────────────
    getSalesCustomers: builder.query({
      query: ({ employeeId = "" } = {}) => ({
        url: "/sales/customers",
        params: { employeeId },
      }),
      providesTags: ["Sales"],
    }),
    getSales: builder.query({
      query: ({ status = "", q = "", salesPersonId = "", page = 1 } = {}) => ({
        url: "/sales/records",
        params: { status, q, salesPersonId, page, limit: 50 },
      }),
      providesTags: ["Sales"],
    }),
    getSalesSellers: builder.query({
      query: () => "/sales/sellers",
      providesTags: ["Sales"],
    }),

    // ── Performance Review (super-admin) ───────────────────────────────────
    getPrConfig: builder.query({
      query: () => "/performance-review/config",
      providesTags: ["PerformanceReview"],
    }),
    getPrPeriods: builder.query({
      query: () => "/performance-review/periods",
      providesTags: ["PerformanceReview"],
    }),
    getPrScoreboard: builder.query({
      query: ({ id } = {}) => `/performance-review/periods/${id}/scoreboard`,
      providesTags: ["PerformanceReview"],
    }),
    getPrEmployee: builder.query({
      query: ({ id, employeeId } = {}) =>
        `/performance-review/periods/${id}/employee/${employeeId}`,
      providesTags: ["PerformanceReview"],
    }),

    // ── Netplus Bank Reconciliation ─────────────────────────────────────────
    getNbrRuns: builder.query({
      query: ({ partnerCode, limit = 60 } = {}) => ({
        url: "/netplus/bank-recon/runs",
        params: { partnerCode, limit },
      }),
      providesTags: ["NetplusBankRecon"],
    }),
    getNbrRun: builder.query({
      query: ({ partnerCode, id } = {}) => ({
        url: `/netplus/bank-recon/runs/${id}`,
        params: { partnerCode },
      }),
      providesTags: (r, e, arg) => [{ type: "NetplusBankRecon", id: arg?.id }],
    }),

    // ── Salary Sheet (super-admin) ────────────────────────────────────────────
    getSsConfig: builder.query({
      query: () => "/salary-sheet/config",
      providesTags: ["SalarySheet"],
    }),
    getSsProfiles: builder.query({
      query: () => "/salary-sheet/profiles",
      providesTags: ["SalarySheet"],
    }),
    getSsProfile: builder.query({
      query: ({ employeeId } = {}) => `/salary-sheet/profiles/${employeeId}`,
      providesTags: ["SalarySheet"],
    }),
    getSsRuns: builder.query({
      // projectId: "" = active header project (default), "all" = every
      // employee, or a project id — the scope picked on the Monthly sheets tab.
      query: ({ projectId, companyId } = {}) => ({
        url: "/salary-sheet/runs",
        params: {
          ...(projectId ? { projectId } : {}),
          ...(companyId ? { companyId } : {}),
        },
      }),
      providesTags: ["SalarySheet"],
    }),
    getSsCompanies: builder.query({
      query: () => "/salary-sheet/companies",
      providesTags: ["SalarySheet"],
    }),
    getSsProjects: builder.query({
      query: () => "/salary-sheet/projects",
      providesTags: ["SalarySheet"],
    }),
    getSsRun: builder.query({
      query: ({ id } = {}) => `/salary-sheet/runs/${id}`,
      providesTags: (r, e, arg) => [{ type: "SalarySheet", id: arg?.id }],
    }),

    // ── Imprest ───────────────────────────────────────────────────────────────
    getImprestSummary: builder.query({
      query: () => "/imprest/summary",
      providesTags: ["Imprest"],
    }),

    getEmployeeImprestLedger: builder.query({
      query: (employeeId) => `/imprest/employee/${employeeId}`,
      providesTags: (result, error, employeeId) => [
        { type: "Imprest", employeeId },
      ],
    }),

    getProjectImprestExpenses: builder.query({
      query: ({ employeeId = "", dateFrom = "", dateTo = "" } = {}) => ({
        url: "/imprest/project-expenses",
        params: { employeeId, dateFrom, dateTo },
      }),
      providesTags: ["Imprest"],
    }),

    // ── MD Schedule ───────────────────────────────────────────────────────────
    getMdSchedules: builder.query({
      query: ({ date = "", status = "", mdId = "" } = {}) => ({
        url: "/schdule/list",
        params: { date, status, mdId },
      }),
      providesTags: ["MdSchedules"],
    }),

    getMdScheduleById: builder.query({
      query: (id) => `/schdule/${id}`,
      providesTags: (result, error, id) => [{ type: "MdSchedules", id }],
    }),

    getManagingDirectors: builder.query({
      query: () => "/schdule/managing-directors",
      providesTags: ["ManagingDirectors"],
    }),

    // ── Sessions ──────────────────────────────────────────────────────────────
    getAllSessions: builder.query({
      query: () => "/sessions",
      providesTags: ["Sessions"],
    }),

    // ── Blogs ─────────────────────────────────────────────────────────────────
    // Blueprint for a paginated query. The component passes { page, limit }.
    getAllBlogs: builder.query({
      query: ({ page = 1, limit = 10 } = {}) =>
        `/blogs?page=${page}&limit=${limit}`,
      providesTags: ["Blogs"],
    }),

    getBlogById: builder.query({
      query: (id) => `/blog/${id}`,
      providesTags: (result, error, id) => [{ type: "Blogs", id }],
    }),

    // ── Companies ─────────────────────────────────────────────────────────────
    getCompanies: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "updatedAt",
        sortOrder = "desc",
      } = {}) => ({
        url: "/users/companies",
        params: { page, limit, search, sortBy, sortOrder },
      }),
      providesTags: ["Companies"],
    }),
    // ── Vehicle Assignments ───────────────────────────────────────────────────
    // All currently active (endDate: null) assignments with employee + vehicle info
    getVehicleAssignments: builder.query({
      query: () => "/vehicle/assignments",
      providesTags: ["VehicleAssignments"],
    }),

    // Full assignment history for one vehicle
    getVehicleHistory: builder.query({
      query: (id) => `/vehicle/${id}/history`,
      providesTags: (r, e, id) => [
        { type: "VehicleAssignments", id },
        { type: "Vehicles", id },
      ],
    }),

    // vehicle
    getVehicles: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "updatedAt",
        sortOrder = "desc",
      } = {}) => ({
        url: "/vehicles",
        params: { page, limit, search, sortBy, sortOrder },
      }),
      providesTags: ["Vehicles"],
    }),
    // ── Company Categories ────────────────────────────────────────────────────
    listCompanyCategories: builder.query({
      query: () => "/admin/company-categories",
      providesTags: ["CompanyCategories"],
    }),

    // ── Contractor Categories ─────────────────────────────────────────────────
    getContractorCategories: builder.query({
      query: () => "/contractor-categories",
      providesTags: ["ContractorCategories"],
    }),

    // ── Contractors ───────────────────────────────────────────────────────────
    getContractors: builder.query({
      query: () => "/contractors",
      providesTags: ["Contractors"],
    }),

    getContractorById: builder.query({
      query: (id) => `/contractor/${id}`,
      providesTags: (result, error, id) => [{ type: "Contractors", id }],
    }),

    // ── Suppliers ─────────────────────────────────────────────────────────────
    getSuppliers: builder.query({
      query: () => "/supplier/list",
      providesTags: ["Suppliers"],
    }),

    getSupplierById: builder.query({
      query: (id) => `/supplier/${id}`,
      providesTags: (result, error, id) => [{ type: "Suppliers", id }],
    }),

    // ── Finance / Expenses ────────────────────────────────────────────────────
    // Paginated daybook — components pass { page, categoryId, dateFrom, dateTo }
    getExpenses: builder.query({
      query: ({
        page = 1,
        categoryId = "",
        dateFrom = "",
        dateTo = "",
        search = "",
        isVehicleExpense = "", // pass "true" to filter vehicle-only entries
      } = {}) => ({
        url: "/finance/daybook",
        params: {
          page,
          categoryId,
          dateFrom,
          dateTo,
          search,
          isVehicleExpense,
        },
      }),
      providesTags: ["Expenses"],
    }),

    // All finance entries for chart aggregation (no filters, high limit)
    getExpensesForChart: builder.query({
      query: () => ({
        url: "/finance/daybook",
        params: { page: 1, limit: 5000 },
      }),
      providesTags: ["Expenses"],
    }),

    // Finance category list — used to populate the "Category" dropdown
    getFinanceCategories: builder.query({
      query: () => "/finance/list",
      providesTags: ["FinanceCategories"],
    }),

    // Full category records — used by the Category Heads management table
    getFinanceCategoriesAll: builder.query({
      query: () => "/finance/categories",
      providesTags: ["FinanceCategories"],
    }),

    // ── Analytics ─────────────────────────────────────────────────────────────
    // Aggregated total expense per category + grand total
    getCategoryExpenseSummaries: builder.query({
      query: () => "/finance/category-summaries",
      providesTags: ["Expenses"],
    }),

    // Date-wise paginated ledger for a single category
    getCategoryDetailLedger: builder.query({
      query: ({ id, page = 1 }) => ({
        url: `/finance/category-detail/${id}`,
        params: { page },
      }),
      providesTags: (r, e, { id }) => [{ type: "Expenses", id }],
    }),

    // All transactions for a category — used for chart aggregation (no pagination)
    getCategoryDetailForChart: builder.query({
      query: (id) => ({
        url: `/finance/category-detail/${id}`,
        params: { page: 1, limit: 5000 },
      }),
      providesTags: (r, e, id) => [{ type: "Expenses", id }],
    }),

    // Aggregated total expense per vehicle + grand total (RBAC: super-admin / Auditor)
    getVehicleExpenseSummaries: builder.query({
      query: () => "/finance/vehicle-summaries",
      providesTags: ["Expenses"],
    }),

    // Date-wise paginated ledger for a single vehicle (RBAC: super-admin / Auditor)
    getVehicleDetailLedger: builder.query({
      query: ({ id, page = 1 }) => ({
        url: `/finance/vehicle-detail/${id}`,
        params: { page },
      }),
      providesTags: (r, e, { id }) => [{ type: "Expenses", id }],
    }),

    // All vehicle expense transactions across all vehicles — used for summary chart aggregation
    getVehicleSummaryForChart: builder.query({
      query: () => ({
        url: "/finance/daybook",
        params: { page: 1, limit: 5000, isVehicleExpense: "true" },
      }),
      providesTags: ["Expenses"],
    }),

    // All transactions for a vehicle — used for chart aggregation (no pagination)
    getVehicleDetailForChart: builder.query({
      query: (id) => ({
        url: `/finance/vehicle-detail/${id}`,
        params: { page: 1, limit: 5000 },
      }),
      providesTags: (r, e, id) => [{ type: "Expenses", id }],
    }),

    // Aggregated totals per contractor (RBAC: super-admin / Auditor)
    getContractorExpenseSummaries: builder.query({
      query: () => "/finance/contractor-summaries",
      providesTags: ["Expenses"],
    }),

    // Date-wise paginated ledger for a single contractor (RBAC: super-admin / Auditor)
    getContractorDetailLedger: builder.query({
      query: ({ id, page = 1 }) => ({
        url: `/finance/contractor-detail/${id}`,
        params: { page },
      }),
      providesTags: (r, e, { id }) => [{ type: "Expenses", id }],
    }),

    // Aggregated totals per supplier (RBAC: super-admin / Auditor)
    getSupplierExpenseSummaries: builder.query({
      query: () => "/finance/supplier-summaries",
      providesTags: ["Expenses"],
    }),

    // Date-wise paginated ledger for a single supplier (RBAC: super-admin / Auditor)
    getSupplierDetailLedger: builder.query({
      query: ({ id, page = 1 }) => ({
        url: `/finance/supplier-detail/${id}`,
        params: { page },
      }),
      providesTags: (r, e, { id }) => [{ type: "Expenses", id }],
    }),

    getSupplierDetailLedger: builder.query({
      query: ({ id, page = 1 }) => ({
        url: `/finance/supplier-detail/${id}`,
        params: { page },
      }),
      providesTags: (r, e, { id }) => [{ type: "Expenses", id }],
    }),
    // ── Labour Types ─────────────────────────────────────────────────────────
    getLabourTypes: builder.query({
      query: () => "/labour-type/list",
      providesTags: ["LabourTypes"],
    }),

    // Aggregated totals per labourer
    getLabourExpenseSummaries: builder.query({
      query: () => "/finance/labour-summaries",
      providesTags: ["LabourPayments"],
    }),

    // Date-wise paginated ledger for a single labourer
    getLabourDetailLedger: builder.query({
      query: ({ id, page = 1 }) => ({
        url: `/finance/labour-detail/${id}`,
        params: { page },
      }),
      providesTags: (r, e, { id }) => [{ type: "LabourPayments", id }],
    }),

    // ── Store / SKU ───────────────────────────────────────────────────────────
    getSkus: builder.query({
      query: () => "/stock/skus",
      providesTags: ["SKUs"],
    }),

    getIssuedToOptions: builder.query({
      query: () => "/stock/issued-to-options",
      providesTags: ["Contractors", "Suppliers"],
    }),

    // current store stock per item: { balances: { [skuId]: number } }
    getStoreBalances: builder.query({
      query: () => "/stock/store/balances",
      providesTags: ["StoreTransactions"],
    }),

    getStoreTransactions: builder.query({
      query: ({ skuId = "", entryType = "", page = 1, limit = 100 } = {}) => ({
        url: "/stock/store/transactions",
        params: { skuId, entryType, page, limit },
      }),
      providesTags: ["StoreTransactions"],
    }),

    getStockEditRequests: builder.query({
      query: (status = "") => ({
        url: "/stock/edit-requests",
        params: status ? { status } : {},
      }),
      providesTags: ["StockEditRequests"],
    }),

    getStockEditRequestsPendingCount: builder.query({
      query: () => "/stock/edit-requests/pending-count",
      providesTags: ["StockEditRequests"],
    }),

    getVehicleFuelLog: builder.query({
      query: (vehicleId) => `/stock/vehicle/${vehicleId}/fuel-log`,
      providesTags: (r, e, id) => [
        { type: "StoreTransactions", id },
        "StoreTransactions",
      ],
    }),

    // All Finance payment records for a specific vehicle (no RBAC gate)
    getVehicleExpenses: builder.query({
      query: (vehicleId) => `/finance/vehicle/${vehicleId}/expenses`,
      providesTags: (r, e, id) => [{ type: "Vehicles", id }, "Expenses"],
    }),

    getVehicleFuelSummary: builder.query({
      query: () => "/stock/vehicle-fuel-summary",
      providesTags: ["StoreTransactions", "Vehicles"],
    }),

    // ── Work Progress DPR ─────────────────────────────────────────────────────
    // Single aggregator for dropdowns (Add / Edit forms)
    getWorkProgressDropdowns: builder.query({
      query: () => "/workprogress/dropdowns",
      providesTags: ["WorkProgressDropdowns"],
    }),

    // Paginated / full list for the List view
    getWorkProgressList: builder.query({
      query: () => "/workprogress",
      providesTags: ["WorkProgressReports"],
    }),

    // Single fully-populated report for Detail and Edit views
    getWorkProgressReport: builder.query({
      query: (id) => `/workprogress/${id}`,
      providesTags: (result, error, id) => [
        { type: "WorkProgressReports", id },
      ],
    }),

    // ── Daily Material Progress Report ───────────────────────────────────────
    getMorningMaterials: builder.query({
      query: (date) => ({ url: "/dpr/morning-materials", params: { date } }),
      providesTags: ["DailyMaterial"],
    }),

    getDailyMaterialList: builder.query({
      query: () => "/dpr/daily-material",
      providesTags: ["DailyMaterial"],
    }),

    getDailyMaterialReport: builder.query({
      query: (id) => `/dpr/daily-material/${id}`,
      providesTags: (r, e, id) => [{ type: "DailyMaterial", id }],
    }),

    // ── Equipment / DPR ──────────────────────────────────────────────────────
    // Minimal project list for the equipment add-form dropdown.
    getEquipmentProjects: builder.query({
      query: () => "/equipment/projects",
      providesTags: ["EquipmentProjects"],
    }),

    // Full equipment list
    getEquipmentList: builder.query({
      query: () => "/equipment/list",
      providesTags: ["Equipment"],
    }),

    // Company assets — active project by default, ?projectId= to switch
    // ("all" is super-admin only).
    getAssets: builder.query({
      query: ({ projectId, search, condition } = {}) => ({
        url: "/assets",
        params: { projectId, search: search || undefined, condition: condition || undefined },
      }),
      providesTags: ["Assets"],
    }),

    // Full equipment detail with service & relocation history
    getEquipmentDetail: builder.query({
      query: (id) => `/equipment/${id}`,
      providesTags: (r, e, id) => [{ type: "Equipment", id }, "Equipment"],
    }),

    // Finance records auto-synced for a specific equipment
    getEquipmentExpenses: builder.query({
      query: (id) => `/equipment/${id}/expenses`,
      providesTags: (r, e, id) => [{ type: "Equipment", id }, "Expenses"],
    }),

    // ── Attendance — calendar, roster, admin views ────────────────────────────
    getMyCalendar: builder.query({
      query: (month) => ({ url: "/attendance/my-calendar", params: { month } }),
      providesTags: ["AttendanceCalendar"],
    }),

    getHolidays: builder.query({
      query: ({ month, from, to } = {}) => ({
        url: "/holidays",
        params: { month, from, to },
      }),
      providesTags: ["Holidays"],
    }),

    // Pass a projectId to look up a specific project's geofence regardless of
    // which project is globally active (used by the settings page).
    getWorkplaceSettings: builder.query({
      query: (projectId) => ({
        url: "/attendance/workplace-settings",
        headers: projectId ? { "x-project-id": projectId } : undefined,
      }),
      providesTags: ["WorkplaceSettings"],
    }),

    getAllWorkplaceSettings: builder.query({
      query: () => "/attendance/workplace-settings/all",
      providesTags: ["WorkplaceSettings"],
    }),

    getSelfieConfig: builder.query({
      query: () => "/attendance/selfie-config",
      providesTags: ["SelfieConfig"],
    }),

    getDailyRoster: builder.query({
      query: ({ date, projectId } = {}) => ({
        url: `/attendance/roster/${date}`,
        params: projectId ? { projectId } : {},
      }),
      providesTags: ["AttendanceRoster"],
    }),

    getWorkingHours: builder.query({
      query: ({ from, to, projectId } = {}) => ({
        url: "/attendance/working-hours",
        params: { from, to, ...(projectId ? { projectId } : {}) },
      }),
      providesTags: ["AttendanceRoster"],
    }),

    getEmployeeMonthView: builder.query({
      query: ({ id, month }) => ({
        url: `/attendance/employee/${id}/calendar`,
        params: { month },
      }),
      providesTags: (r, e, { id }) => [{ type: "AttendanceCalendar", id }],
    }),

    // ── Leave ─────────────────────────────────────────────────────────────────
    getMyLeaves: builder.query({
      query: (status = "") => ({
        url: "/leave/list",
        params: status ? { status } : {},
      }),
      providesTags: ["Leaves"],
    }),

    getLeaveById: builder.query({
      query: (id) => `/leave/${id}`,
      providesTags: (r, e, id) => [{ type: "Leaves", id }],
    }),

    // ── Admin Projects ────────────────────────────────────────────────────────
    getProjects: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "created",
        sortOrder = "desc",
      } = {}) => ({
        url: "/admin/projects",
        params: { page, limit, search, sortBy, sortOrder },
      }),
      providesTags: ["Projects"],
    }),

    // Full project list for the dashboard project-matrix (no pagination — fetches all)
    getAllProjectsForMatrix: builder.query({
      query: () => ({
        url: "/admin/projects",
        params: {
          page: 1,
          limit: 500,
          sortBy: "projectName",
          sortOrder: "asc",
        },
      }),
      providesTags: ["Projects"],
    }),

    // Projects assigned to the currently logged-in user
    getMyProjects: builder.query({
      query: () => "/user/my-projects",
      providesTags: ["UserProjects"],
    }),
  }),
});

// ─── Auto-generated hooks (import these in your components) ──────────────────

export const {
  useGetCurrentUserQuery,
  useGetExpensesForChartQuery,
  useGetVehicleDetailForChartQuery,
  useGetVehicleSummaryForChartQuery,
  useGetCategoryDetailForChartQuery,
  useGetSkusQuery,
  useGetIssuedToOptionsQuery,
  useGetStoreTransactionsQuery,
  useGetStockEditRequestsQuery,
  useGetStockEditRequestsPendingCountQuery,
  useGetVehicleFuelLogQuery,
  useGetVehicleExpensesQuery,
  useGetVehicleFuelSummaryQuery,
  useGetVehicleAssignmentsQuery,
  useGetVehicleHistoryQuery,
  useGetDashboardStatsQuery,
  useGetAllCustomersQuery,
  useGetCustomerQuery,
  useGetAllStaffQuery,
  useGetStaffByIdQuery,
  useGetTechnicalEmployeesQuery,
  useGetStaffReportsQuery,
  useGetPmCombinedReportQuery,
  useGetStaffReportByIdQuery,
  useGetEmployeeStaffReportsQuery,
  useGetMyBackdateGrantsQuery,
  useGetBackdateGrantsQuery,
  useGetStoreBalancesQuery,
  useGetRealEstateTeamsQuery,
  useGetRealEstateTeamOptionsQuery,
  useGetNetplusTeamsQuery,
  useGetDailySummaryQuery,
  useGetNetplusTeamOptionsQuery,
  useGetNetplusClubReportQuery,
  useGetRealEstateClubReportQuery,
  useGetMovementRegisterQuery,
  useGetAdvanceSummariesQuery,
  useGetEmployeeAdvanceLedgerQuery,
  useGetCpDatabaseQuery,
  useGetChatContactsQuery,
  useGetChatConversationsQuery,
  useGetChatUnreadCountQuery,
  useGetNetplusReceiptsQuery,
  useGetNetplusSummaryQuery,
  useGetNetplusTransfersQuery,
  useGetNetplusModemEntriesQuery,
  useGetNetplusModemSummaryQuery,
  useGetNcsPartnersQuery,
  useGetNcsDashboardQuery,
  useGetNcsTrendQuery,
  useGetNcsDeactivationForecastQuery,
  useGetNcsCustomersQuery,
  useGetNcsNewConnectionsQuery,
  useGetNcsCustomerQuery,
  useGetNcsTransitionsQuery,
  useGetNcsFollowupsQuery,
  useGetNcsFollowupSummaryQuery,
  useGetNcsStillInactiveSinceYesterdayQuery,
  useGetNcsActivatedQuery,
  useGetNcsUploadsQuery,
  useGetNcsRechargedTodayQuery,
  useGetNcsDailyReportQuery,
  useGetNcsOverdueBucketsQuery,
  useGetNcsDiagQuery,
  useGetNcsTimeseriesQuery,
  useGetNcsIncentiveQuery,
  useGetNcsIncentiveNewConnectionsQuery,
  useGetNcsIncentivePlansQuery,
  useGetNcsIncentiveConfigQuery,
  useGetNcsTeamQuery,
  useGetNcsModemRecoveryQuery,
  useGetReconciliationRunsQuery,
  useGetReconciliationRunQuery,
  useGetNcsFreelancerPaymentsQuery,
  useGetNcsAuditQuery,
  useGetNcsPerformanceQuery,
  useGetNcsEmployeeDailyQuery,
  useGetNcsMyDailyQuery,
  useGetNcmpComplaintsQuery,
  useGetNcmpUploadsQuery,
  useGetNcmpSummaryQuery,
  useGetNcmpResolversQuery,
  useGetSalesCustomersQuery,
  useGetSalesQuery,
  useGetSalesSellersQuery,
  useGetPrConfigQuery,
  useGetPrPeriodsQuery,
  useGetPrScoreboardQuery,
  useGetPrEmployeeQuery,
  useGetNbrRunsQuery,
  useGetNbrRunQuery,
  useGetSsConfigQuery,
  useGetSsProfilesQuery,
  useGetSsProfileQuery,
  useGetSsRunsQuery,
  useGetSsProjectsQuery,
  useGetSsCompaniesQuery,
  useGetSsRunQuery,
  useGetImprestSummaryQuery,
  useGetEmployeeImprestLedgerQuery,
  useGetProjectImprestExpensesQuery,
  useGetMdSchedulesQuery,
  useGetMdScheduleByIdQuery,
  useGetManagingDirectorsQuery,
  useGetAllSessionsQuery,
  useGetAllBlogsQuery,
  useGetBlogByIdQuery,
  useGetCompaniesQuery,
  useListCompanyCategoriesQuery,
  useGetProjectsQuery,
  useGetSuppliersQuery,
  useGetSupplierByIdQuery,
  useGetContractorCategoriesQuery,
  useGetContractorsQuery,
  useGetContractorByIdQuery,
  useGetVehiclesQuery,
  useGetExpensesQuery,
  useGetFinanceCategoriesQuery,
  useGetFinanceCategoriesAllQuery,
  useGetCategoryExpenseSummariesQuery,
  useGetCategoryDetailLedgerQuery,
  useGetVehicleExpenseSummariesQuery,
  useGetVehicleDetailLedgerQuery,
  useGetContractorExpenseSummariesQuery,
  useGetContractorDetailLedgerQuery,
  useGetSupplierExpenseSummariesQuery,
  useGetSupplierDetailLedgerQuery,
  useGetLabourTypesQuery,
  useGetLabourExpenseSummariesQuery,
  useGetLabourDetailLedgerQuery,
  useGetEquipmentProjectsQuery,
  useGetEquipmentListQuery,
  useGetAssetsQuery,
  useGetEquipmentDetailQuery,
  useGetEquipmentExpensesQuery,
  useGetWorkProgressDropdownsQuery,
  useGetWorkProgressListQuery,
  useGetWorkProgressReportQuery,
  useGetMorningMaterialsQuery,
  useGetDailyMaterialListQuery,
  useGetDailyMaterialReportQuery,
  useGetAllProjectsForMatrixQuery,
  useGetMyProjectsQuery,
  useGetMyCalendarQuery,
  useGetHolidaysQuery,
  useGetWorkplaceSettingsQuery,
  useGetAllWorkplaceSettingsQuery,
  useGetSelfieConfigQuery,
  useGetDailyRosterQuery,
  useGetWorkingHoursQuery,
  useGetEmployeeMonthViewQuery,
  useGetMyLeavesQuery,
  useGetLeaveByIdQuery,
} = appApi;
